import React, { useState, useEffect } from 'react';
import { getTasks, generateClientSchedule } from '../api';
import LoadingState from './LoadingState';
import { useTheme } from '../hooks/useTheme';

const ClientViewTab = ({ projectId, refreshSummary }) => {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [exportData, setExportData] = useState(null);

  const t = useTheme();

  const fetchClientTasks = async () => {
    try {
      setLoading(true);
      const allTasks = await getTasks(projectId);
      const clientVisibleTasks = allTasks.filter(task => task.client_visible);
      setTasks(clientVisibleTasks);
      setError(null);
    } catch (err) {
      setError('Failed to fetch client schedule');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchClientTasks();
  }, [projectId]);

  const handleExportSchedule = async () => {
    try {
      setExporting(true);
      const data = await generateClientSchedule({ project_id: projectId });
      setExportData(data);
    } catch (err) {
      setError('Failed to export client schedule');
      console.error(err);
    } finally {
      setExporting(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-US');
  };

  const calculateDuration = (startDate, finishDate) => {
    if (!startDate || !finishDate) return '-';
    const start = new Date(startDate);
    const finish = new Date(finishDate);
    const days = Math.ceil((finish - start) / (1000 * 60 * 60 * 24)) + 1;
    return days > 0 ? `${days} days` : '-';
  };

  const getStatusBadgeClass = (status) => {
    switch (status?.toLowerCase()) {
      case 'completed':
      case 'closed':
        return 'badge badge-success';
      case 'in progress':
      case 'active':
        return 'badge badge-primary';
      case 'on hold':
        return 'badge badge-warning';
      case 'not started':
      case 'pending':
        return 'badge badge-secondary';
      default:
        return 'badge badge-secondary';
    }
  };

  if (loading) {
    return <LoadingState message="Loading client schedule…" />;
  }

  return (
    <div className="container">
      <div className="header-section">
        <h2>Client Schedule</h2>
        <button
          className="btn btn-primary"
          onClick={handleExportSchedule}
          disabled={exporting}
        >
          {exporting ? 'Exporting...' : 'Export Client Schedule'}
        </button>
      </div>

      {error && <div className="alert alert-error">{error}</div>}

      {/* Export Preview */}
      {exportData && (
        <div className="card" style={{ marginBottom: '30px', backgroundColor: t.bgAlt }}>
          <h3>Export Preview</h3>
          <div style={{ fontSize: '15.5px', color: t.textSecondary }}>
            <p><strong>File Name:</strong> {exportData.filename}</p>
            <p><strong>Format:</strong> {exportData.format}</p>
            <p><strong>Tasks Included:</strong> {exportData.task_count}</p>
            <p><strong>Generated:</strong> {new Date(exportData.generated_at).toLocaleString('en-US')}</p>
            {exportData.download_url && (
              <a href={exportData.download_url} target="_blank" rel="noopener noreferrer" className="btn btn-sm btn-primary" style={{ marginTop: '10px' }}>
                Download File
              </a>
            )}
          </div>
        </div>
      )}

      {/* Client Schedule Table */}
      <div className="card">
        <div style={{ overflowX: 'auto', overflowY: 'hidden' }}>
        <table className="table">
          <thead>
            <tr>
              <th>Phase</th>
              <th>Task Name</th>
              <th>Start Date</th>
              <th>Finish Date</th>
              <th>Duration</th>
              <th>% Complete</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {tasks.length === 0 ? (
              <tr>
                <td colSpan="7" style={{ textAlign: 'center', padding: '20px' }}>
                  No client-visible tasks available.
                </td>
              </tr>
            ) : (
              tasks.map(task => (
                <tr key={task.id}>
                  <td>{task.phase || '-'}</td>
                  <td>{task.task_name}</td>
                  <td>{formatDate(task.start_date)}</td>
                  <td>{formatDate(task.finish_date)}</td>
                  <td>{calculateDuration(task.start_date, task.finish_date)}</td>
                  <td>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <div
                        style={{
                          flex: 1,
                          backgroundColor: t.cardBorder,
                          height: '20px',
                          borderRadius: '4px',
                          overflow: 'hidden',
                          minWidth: '100px'
                        }}
                      >
                        <div
                          style={{
                            backgroundColor: '#4CAF50',
                            height: '100%',
                            width: `${task.percent_complete || 0}%`,
                            transition: 'width 0.3s'
                          }}
                        />
                      </div>
                      <span style={{ minWidth: '35px', textAlign: 'right', fontWeight: '500' }}>
                        {task.percent_complete || 0}%
                      </span>
                    </div>
                  </td>
                  <td>
                    <span className={getStatusBadgeClass(task.status)}>
                      {task.status || 'Not Started'}
                    </span>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
        </div>
      </div>

      {/* Summary Info */}
      {tasks.length > 0 && (
        <div className="card" style={{ marginTop: '20px' }}>
          <h3>Schedule Summary</h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px', marginTop: '16px' }}>
            <div style={{ padding: '12px', backgroundColor: t.bgAlt, borderRadius: '4px' }}>
              <p style={{ margin: 0, fontSize: '13.5px', color: t.textSecondary }}>Total Tasks</p>
              <p style={{ margin: 0, fontSize: '21.5px', fontWeight: 'bold' }}>{tasks.length}</p>
            </div>
            <div style={{ padding: '12px', backgroundColor: t.bgAlt, borderRadius: '4px' }}>
              <p style={{ margin: 0, fontSize: '13.5px', color: t.textSecondary }}>Completed</p>
              <p style={{ margin: 0, fontSize: '21.5px', fontWeight: 'bold' }}>
                {tasks.filter(t => t.status?.toLowerCase() === 'completed' || t.status?.toLowerCase() === 'closed').length}
              </p>
            </div>
            <div style={{ padding: '12px', backgroundColor: t.bgAlt, borderRadius: '4px' }}>
              <p style={{ margin: 0, fontSize: '13.5px', color: t.textSecondary }}>In Progress</p>
              <p style={{ margin: 0, fontSize: '21.5px', fontWeight: 'bold' }}>
                {tasks.filter(t => t.status?.toLowerCase() === 'in progress' || t.status?.toLowerCase() === 'active').length}
              </p>
            </div>
            <div style={{ padding: '12px', backgroundColor: t.bgAlt, borderRadius: '4px' }}>
              <p style={{ margin: 0, fontSize: '13.5px', color: t.textSecondary }}>Not Started</p>
              <p style={{ margin: 0, fontSize: '21.5px', fontWeight: 'bold' }}>
                {tasks.filter(t => t.status?.toLowerCase() === 'not started' || t.status?.toLowerCase() === 'pending' || !t.status).length}
              </p>
            </div>
            <div style={{ padding: '12px', backgroundColor: t.bgAlt, borderRadius: '4px' }}>
              <p style={{ margin: 0, fontSize: '13.5px', color: t.textSecondary }}>Overall Progress</p>
              <p style={{ margin: 0, fontSize: '21.5px', fontWeight: 'bold' }}>
                {Math.round(tasks.reduce((sum, t) => sum + (t.percent_complete || 0), 0) / tasks.length)}%
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ClientViewTab;
